import { useState, useEffect, useCallback } from 'react';

const BASE = '/spring/builder/api';

const req = async (path, opts = {}) => {
  const res = await fetch(`${BASE}${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(opts.headers || {}) },
    ...opts,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || res.statusText);
  return data;
};

const ROLES = [
  {
    value: 'admin',
    label: 'Admin',
    color: '#137333', bg: '#e6f4ea',
    desc: 'Full access — add, edit, delete + manage team',
    menu: ['dashboard','projects','materials','purchases','inventory','issues','work','employees','payments','reports','settings','documents','users'],
  },
  {
    value: 'adduser',
    label: 'Data Entry',
    color: '#b45309', bg: '#fef3c7',
    desc: 'Can add & edit records — no delete, no team management',
    menu: ['projects','materials','purchases','inventory','issues','work','employees','payments','reports','documents'],
  },
  {
    value: 'readonly',
    label: 'Read Only',
    color: '#1a73e8', bg: '#e8f0fe',
    desc: 'View all data and reports — no add, edit or delete',
    menu: ['projects','inventory','reports','documents'],
  },
];

const MENU_LABELS = {
  dashboard: '🏠 Dashboard',
  projects:  '🏗️ Projects',
  materials: '📦 Materials',
  purchases: '🛒 Purchases',
  inventory: '🏭 Inventory',
  issues:    '🔄 Issue Materials',
  work:      '👷 Work Completion',
  employees: '👥 Employees',
  payments:  '💰 Payments',
  reports:   '📊 Reports',
  settings:  '⚙️ Settings',
  documents: '📄 Documents',
  users:     '👤 Team',
};

const EMPTY = { name: '', username: '', email: '', phone: '', designation: '', role: 'readonly', isActive: true, notes: '', password: '' };
const PWD_EMPTY = { newPassword: '', confirmPassword: '' };

function roleBadge(r) {
  const found = ROLES.find(x => x.value === r) || ROLES[2];
  return (
    <span style={{ background: found.bg, color: found.color, borderRadius: 12, padding: '2px 10px', fontSize: 12, fontWeight: 600 }}>
      {found.label}
    </span>
  );
}

export default function BuilderUsers() {
  const [users, setUsers]     = useState([]);
  const [form, setForm]       = useState(EMPTY);
  const [editId, setEditId]   = useState(null);
  const [tab, setTab]         = useState('list');
  const [loading, setLoading] = useState(true);
  const [err, setErr]         = useState('');
  const [saving, setSaving]   = useState(false);
  const [pwdForm, setPwdForm]     = useState(PWD_EMPTY);
  const [pwdErr, setPwdErr]       = useState('');
  const [pwdOk, setPwdOk]         = useState('');
  const [pwdSaving, setPwdSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    req('/users').then(rows => setUsers(Array.isArray(rows) ? rows : [])).catch(() => setUsers([])).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.name.trim()) { setErr('Name is required'); return; }
    if (!editId && !form.username.trim()) { setErr('Username is required'); return; }
    if (!editId && !form.password) { setErr('Password is required for new team members'); return; }
    setSaving(true); setErr('');
    try {
      if (editId) await req(`/users/${editId}`, { method: 'PUT', body: JSON.stringify(form) });
      else        await req('/users',            { method: 'POST', body: JSON.stringify(form) });
      setForm(EMPTY); setEditId(null); setTab('list'); load();
    } catch (e) { setErr(e.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  const handleEdit = (u) => {
    setForm({
      name: u.name || '', username: u.username || '', email: u.email || '',
      phone: u.phone || '', designation: u.designation || '',
      role: u.role || 'readonly', isActive: u.is_active !== 0, notes: u.notes || '', password: '',
    });
    setEditId(u.id); setTab('form');
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Remove "${name}" from the team?`)) return;
    await req(`/users/${id}`, { method: 'DELETE' }); load();
  };

  const handlePwdChange = async () => {
    setPwdErr(''); setPwdOk('');
    if (!pwdForm.newPassword) { setPwdErr('New password is required'); return; }
    if (pwdForm.newPassword.length < 6) { setPwdErr('Minimum 6 characters'); return; }
    if (pwdForm.newPassword !== pwdForm.confirmPassword) { setPwdErr('Passwords do not match'); return; }
    setPwdSaving(true);
    try {
      const data = await req('/users/change-password', { method: 'POST', body: JSON.stringify(pwdForm) });
      if (data.success) { setPwdOk('Password changed successfully!'); setPwdForm(PWD_EMPTY); }
      else setPwdErr(data.error || 'Password change failed');
    } catch (e) { setPwdErr(e.message || 'Failed'); }
    finally { setPwdSaving(false); }
  };

  const selectedRole = ROLES.find(r => r.value === form.role) || ROLES[2];

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#718096' }}>Loading team…</div>;

  return (
    <div>
      <div className="tabs">
        <button className={`tab${tab === 'list' ? ' active' : ''}`}
          onClick={() => { setTab('list'); setEditId(null); setForm(EMPTY); }}>
          👥 Team ({users.length})
        </button>
        <button className={`tab${tab === 'form' ? ' active' : ''}`} onClick={() => { setForm(EMPTY); setEditId(null); setTab('form'); }}>
          {editId ? '✏️ Edit Member' : '➕ Add Member'}
        </button>
        <button className={`tab${tab === 'pwd' ? ' active' : ''}`}
          onClick={() => { setTab('pwd'); setPwdErr(''); setPwdOk(''); setPwdForm(PWD_EMPTY); }}>
          🔑 Change Password
        </button>
      </div>

      {/* ── Team list ── */}
      {tab === 'list' && (
        <div>
          {/* Role legend */}
          <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
            {ROLES.map(r => (
              <div key={r.value} style={{ background: r.bg, border: `1px solid ${r.color}30`, borderRadius: 8, padding: '8px 14px', fontSize: 13 }}>
                <span style={{ color: r.color, fontWeight: 700 }}>{r.label}</span>
                <span style={{ color: '#5f6368', marginLeft: 8 }}>{r.desc}</span>
              </div>
            ))}
          </div>

          <div className="card">
            <div className="card-header">
              <div className="card-title">👥 Team Members</div>
              <button className="btn btn-primary" style={{ padding: '5px 16px', fontSize: 13 }}
                onClick={() => { setForm(EMPTY); setEditId(null); setTab('form'); }}>
                ➕ Add Member
              </button>
            </div>
            <div className="card-body" style={{ padding: 0, overflowX: 'auto' }}>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Username</th>
                    <th>Designation</th>
                    <th>Phone</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Since</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.length === 0 && (
                    <tr><td colSpan={8} style={{ textAlign: 'center', color: '#888', padding: 32 }}>
                      No team members yet — click <strong>Add Member</strong> to register the first employee
                    </td></tr>
                  )}
                  {users.map(u => (
                    <tr key={u.id}>
                      <td><strong>{u.name}</strong></td>
                      <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{u.username || '—'}</td>
                      <td style={{ fontSize: 12 }}>{u.designation || '—'}</td>
                      <td style={{ fontSize: 12 }}>{u.phone || '—'}</td>
                      <td>{roleBadge(u.role)}</td>
                      <td>
                        {u.is_active
                          ? <span style={{ background: '#e6f4ea', color: '#137333', borderRadius: 12, padding: '2px 8px', fontSize: 12, fontWeight: 600 }}>Active</span>
                          : <span style={{ background: '#f1f3f4', color: '#9aa0a6', borderRadius: 12, padding: '2px 8px', fontSize: 12, fontWeight: 600 }}>Inactive</span>}
                      </td>
                      <td style={{ fontSize: 12, color: '#888' }}>{u.created_at || '—'}</td>
                      <td>
                        <button className="btn-sm btn-secondary" onClick={() => handleEdit(u)} style={{ marginRight: 4 }}>Edit</button>
                        <button className="btn-sm btn-danger" onClick={() => handleDelete(u.id, u.name)}>Remove</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ── Change password ── */}
      {tab === 'pwd' && (
        <div className="card" style={{ maxWidth: 440 }}>
          <div className="card-header"><div className="card-title">🔑 Change My Password</div></div>
          <div className="card-body">
            <div style={{ marginBottom: 16, padding: '10px 14px', background: '#e8f0fe', borderRadius: 8, fontSize: 13, color: '#1a73e8' }}>
              This changes the password for your team member login account.
            </div>
            {pwdErr && <div style={{ marginBottom: 12, padding: '8px 14px', background: '#fce8e6', color: '#c5221f', borderRadius: 6, fontSize: 13 }}>⚠️ {pwdErr}</div>}
            {pwdOk  && <div style={{ marginBottom: 12, padding: '8px 14px', background: '#e6f4ea', color: '#137333', borderRadius: 6, fontSize: 13, fontWeight: 600 }}>✅ {pwdOk}</div>}
            <div className="form-group">
              <label className="form-label">New Password *</label>
              <input className="form-control" type="password" value={pwdForm.newPassword}
                onChange={e => setPwdForm(f => ({ ...f, newPassword: e.target.value }))}
                placeholder="Minimum 6 characters" />
              {pwdForm.newPassword && pwdForm.newPassword.length < 6 &&
                <div style={{ fontSize: 11, color: '#c5221f', marginTop: 3 }}>Minimum 6 characters</div>}
            </div>
            <div className="form-group" style={{ marginTop: 12 }}>
              <label className="form-label">Confirm New Password *</label>
              <input className="form-control" type="password" value={pwdForm.confirmPassword}
                onChange={e => setPwdForm(f => ({ ...f, confirmPassword: e.target.value }))}
                placeholder="Re-enter new password"
                onKeyDown={e => e.key === 'Enter' && handlePwdChange()} />
              {pwdForm.confirmPassword && pwdForm.newPassword !== pwdForm.confirmPassword &&
                <div style={{ fontSize: 11, color: '#c5221f', marginTop: 3 }}>Passwords do not match</div>}
            </div>
            <div style={{ marginTop: 18 }}>
              <button className="btn btn-primary" onClick={handlePwdChange} disabled={pwdSaving}>
                {pwdSaving ? 'Updating…' : '🔑 Update Password'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Add / Edit form ── */}
      {tab === 'form' && (
        <div className="card" style={{ maxWidth: 720 }}>
          <div className="card-header">
            <div className="card-title">{editId ? '✏️ Edit Team Member' : '➕ Add Team Member'}</div>
          </div>
          <div className="card-body">
            {err && <div style={{ marginBottom: 16, padding: '8px 14px', background: '#fce8e6', color: '#c5221f', borderRadius: 6, fontSize: 13 }}>⚠️ {err}</div>}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div className="form-group">
                <label className="form-label">Full Name *</label>
                <input className="form-control" value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Ravi Kumar" />
              </div>
              <div className="form-group">
                <label className="form-label">Login Username {!editId && '*'}</label>
                <input className="form-control" value={form.username} onChange={e => set('username', e.target.value)}
                  placeholder="Used to log in" disabled={!!editId} />
                {editId && <div style={{ fontSize: 11, color: '#888', marginTop: 3 }}>Username cannot be changed after creation</div>}
              </div>
              <div className="form-group">
                <label className="form-label">Email</label>
                <input className="form-control" type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="email@example.com" />
              </div>
              <div className="form-group">
                <label className="form-label">Phone</label>
                <input className="form-control" value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="+91 9XXXXXXXXX" />
              </div>
              <div className="form-group">
                <label className="form-label">Designation</label>
                <input className="form-control" value={form.designation} onChange={e => set('designation', e.target.value)} placeholder="e.g. Site Supervisor, Accountant" />
              </div>
              <div className="form-group">
                <label className="form-label">Role *</label>
                <select className="form-control" value={form.role} onChange={e => set('role', e.target.value)}>
                  {ROLES.map(r => <option key={r.value} value={r.value}>{r.label} — {r.desc}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">{editId ? 'New Password' : 'Password *'}</label>
                <input className="form-control" type="password" value={form.password}
                  onChange={e => set('password', e.target.value)}
                  placeholder={editId ? 'Leave blank to keep current' : 'Set a login password'} />
              </div>
              <div className="form-group">
                <label className="form-label">Notes</label>
                <input className="form-control" value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Optional notes" />
              </div>
              <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <input type="checkbox" id="isActive" checked={form.isActive} onChange={e => set('isActive', e.target.checked)} style={{ width: 16, height: 16 }} />
                <label htmlFor="isActive" className="form-label" style={{ margin: 0 }}>Active (can log in)</label>
              </div>
            </div>

            {/* Role access summary */}
            <div style={{ marginTop: 16, background: selectedRole.bg, borderRadius: 8, padding: '12px 16px', fontSize: 13 }}>
              <div style={{ fontWeight: 700, color: selectedRole.color, marginBottom: 8 }}>
                {selectedRole.label} — Menu Access
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {Object.keys(MENU_LABELS).map(key => {
                  const allowed = selectedRole.menu.includes(key);
                  return (
                    <span key={key} style={{
                      padding: '2px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600,
                      background: allowed ? selectedRole.color : '#e8eaed',
                      color: allowed ? '#fff' : '#9aa0a6',
                    }}>
                      {MENU_LABELS[key]}
                    </span>
                  );
                })}
              </div>
            </div>

            <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : editId ? '💾 Update' : '➕ Add Member'}
              </button>
              <button className="btn btn-secondary" onClick={() => { setTab('list'); setEditId(null); setForm(EMPTY); setErr(''); }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
